'use client';

import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { listChats } from '../../lib/api/meridian-api-client.js';
import type { ChatSummary } from '../../types/index.js';

export interface UseChatsQueryOptions {
  apiBaseUrl?: string;
  status?: 'active' | 'archived' | 'all';
  limit?: number;
  enabled?: boolean;
  entityType?: string;
  entityId?: string;
}

export interface UseChatsQueryResult {
  data: ChatSummary[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * TanStack Query hook for fetching chats with infinite pagination
 * 
 * Features:
 * - Automatic caching with 1-minute stale time
 * - Infinite pagination support
 * - Automatic refetch on window focus
 * - Exponential backoff retry on failures
 * - Prevents duplicate requests
 */
export function useChatsQuery(options: UseChatsQueryOptions = {}): UseChatsQueryResult {
  const {
    apiBaseUrl = '/api/meridian',
    status = 'all',
    limit = 10,
    enabled = true,
    entityType,
    entityId,
  } = options;

  const query = useInfiniteQuery({
    queryKey: ['chats', { status, limit, entityType, entityId }],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const response = await listChats(apiBaseUrl, {
        status,
        limit,
        cursor: pageParam,
        entityType,
        entityId,
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch chats');
      }

      return {
        chats: response.data?.chats || [],
        cursor: response.data?.cursor || null,
        hasMore: response.data?.hasMore || false,
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 30000),
  });

  // Flatten pages into single array and deduplicate by ID
  // This prevents duplicate keys in React when the same chat appears in multiple pages
  // Also filters out Slack DMs and mentions (chats with titles starting with "Slack DM" or "Slack Mention")
  const allChats = useMemo(() => {
    const flattened = query.data?.pages.flatMap((page: { chats: ChatSummary[] }) => page.chats) || [];
    
    // Use Map to deduplicate by ID, keeping the last occurrence
    const uniqueChatsMap = new Map<string, ChatSummary>();
    for (const chat of flattened) {
      // Filter out Slack DMs and mentions - they have titles starting with "Slack DM" or "Slack Mention"
      const titleLower = chat.title.toLowerCase();
      if (!titleLower.startsWith('slack dm') && !titleLower.startsWith('slack mention')) {
        uniqueChatsMap.set(chat.id, chat);
      }
    }
    const result = Array.from(uniqueChatsMap.values());
    
    return result;
  }, [query.data?.pages]);

  return {
    data: allChats,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasNextPage: query.hasNextPage ?? false,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: async () => {
      await query.fetchNextPage();
    },
    refetch: async () => {
      await query.refetch();
    },
  };
}
